import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import NavigationV2 from '@segment/templates/Navigation2'
import LogoTiles from '@segment/templates/LogoTiles'
import DividerSection from '@segment/templates/DividerSection'

import { useFastGrowingCompaniesQuery } from '@segment/api/routes/partners/queries'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import LaunchIntegration from './components/LaunchIntegration'
import Features from './components/Features'
import Testimonial from './components/Testimonial'
import FastGrowingCompanies from './components/FastGrowingCompanies'
import LeverageEvents from './components/LeverageEvents'
import BuildIntegration from './components/BuildIntegration'
import ExcitedToBuild from './components/ExcitedToBuild'

import ogImageJpeg from './assets/OgDeveloperCenter.png'

export interface DeveloperPortalProps {
  amount: number
}
const DeveloperPortal: FC<PropsWithChildren<DeveloperPortalProps>> = ({ amount }) => {
  const { data: fastGrowingCompanies } = useFastGrowingCompaniesQuery({ args: [{ amount }] })

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle="Developer Portal | Twilio Segment"
        pageDescription="Build a Twilio Segment integration in a few hours through the Twilio Segment Developer Center -- test, submit, and maintain your Twilio Segment integration and OAuth flow."
        pageOG={ogImageJpeg}
      >
        <NavigationV2 />
        <Hero />
        <Main>
          <LeverageEvents />
          <Features />
          <DividerSection />
          <ExcitedToBuild />
          <LightGrayBackground>
            <BuildIntegration />
          </LightGrayBackground>
          <LaunchIntegration />
          <LightGrayBackground>
            <FastGrowingCompanies entries={fastGrowingCompanies} />
            <Testimonial />
            <LogoTiles />
          </LightGrayBackground>
        </Main>
        <Footer />
      </Layout>
    </AppearanceProvider>
  )
}

const Main = styled.main`
  /* Give a main content a background color, so sections don't flash from white background */
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
`

const LightGrayBackground = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

export default DeveloperPortal
